/**
 * Task Dependency CRUD Service - Phase 4 Architecture
 *
 * Encapsulates CRUD operations for task dependencies.
 * Extracted from TaskDependencyService to reduce single-responsibility violations.
 */

import { typedSupabase } from "@/lib/typed-supabase";
import { logger } from "@/lib/logger";
import { IService, ServiceResponse } from "@/services/core/service-registry";
import type {
  CreateTaskDependencyRequest,
  TaskDependency,
  UpdateTaskDependencyRequest,
} from "@/types/time-management";

export class TaskDependencyCrudService implements IService {
  public readonly name = "TaskDependencyCrudService";
  private readonly supabase = typedSupabase;

  async initialize(): Promise<void> {
    logger.info("✅ TaskDependencyCrudService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("✅ TaskDependencyCrudService disposed");
  }

  async createDependency(
    request: CreateTaskDependencyRequest
  ): Promise<ServiceResponse<TaskDependency>> {
    try {
      const { data, error } = await this.supabase
        .from("task_dependencies")
        .insert({
          predecessor_task_id: request.predecessor_task_id,
          successor_task_id: request.successor_task_id,
          dependency_type: request.dependency_type,
          lag_time: request.lag_time ?? 0,
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data as TaskDependency };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async getDependency(
    dependencyId: string
  ): Promise<ServiceResponse<TaskDependency | null>> {
    try {
      const { data, error } = await this.supabase
        .from("task_dependencies")
        .select("*")
        .eq("id", dependencyId)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          return { success: true, data: null };
        }
        return { success: false, error: error.message };
      }

      return { success: true, data: data as TaskDependency };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async getTaskDependencies(taskId: string): Promise<
    ServiceResponse<{
      predecessors: TaskDependency[];
      successors: TaskDependency[];
    }>
  > {
    try {
      const [predecessorsResult, successorsResult] = await Promise.all([
        this.supabase
          .from("task_dependencies")
          .select("*")
          .eq("successor_task_id", taskId),
        this.supabase
          .from("task_dependencies")
          .select("*")
          .eq("predecessor_task_id", taskId),
      ]);

      if (predecessorsResult.error || successorsResult.error) {
        return {
          success: false,
          error:
            (predecessorsResult.error?.message ??
            successorsResult.error?.message) ??
            "Unknown error",
        };
      }

      return {
        success: true,
        data: {
          predecessors: (predecessorsResult.data as TaskDependency[]) || [],
          successors: (successorsResult.data as TaskDependency[]) || [],
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async getAllDependencies(): Promise<ServiceResponse<TaskDependency[]>> {
    try {
      const { data, error } = await this.supabase
        .from("task_dependencies")
        .select("*");

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data as TaskDependency[] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async updateDependency(
    dependencyId: string,
    updates: UpdateTaskDependencyRequest
  ): Promise<ServiceResponse<TaskDependency>> {
    try {
      const { data, error } = await this.supabase
        .from("task_dependencies")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", dependencyId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data as TaskDependency };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async deleteDependency(dependencyId: string): Promise<ServiceResponse<void>> {
    try {
      const { error } = await this.supabase
        .from("task_dependencies")
        .delete()
        .eq("id", dependencyId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: undefined };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async createMultipleDependencies(
    dependencies: CreateTaskDependencyRequest[]
  ): Promise<ServiceResponse<TaskDependency[]>> {
    try {
      const results: TaskDependency[] = [];
      const errors: string[] = [];

      for (const dependency of dependencies) {
        const result = await this.createDependency(dependency);
        if (!result.success) {
          errors.push(
            `${dependency.predecessor_task_id} → ${dependency.successor_task_id}: ${result.error}`
          );
        } else if (result.data) {
          results.push(result.data);
        }
      }

      if (errors.length > 0) {
        return {
          success: false,
          error: `${errors.length} dependencies failed: ${errors.join(", ")}`,
        };
      }

      return { success: true, data: results };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async deleteMultipleDependencies(
    dependencyIds: string[]
  ): Promise<ServiceResponse<void>> {
    try {
      const { error } = await this.supabase
        .from("task_dependencies")
        .delete()
        .in("id", dependencyIds);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: undefined };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }
}
